import type { Auth_LoginClients_Query$data } from '@/graphql/queries/Auth.query';
import type { DeepNonNullable } from 'ts-essentials';

export type SSOLoginClient = DeepNonNullable<Auth_LoginClients_Query$data['loginClients']>[number];

export const navigateToSSOLoginUrl = async (provider: SSOLoginClient, email?: string): Promise<void> => {
  if (!provider?.authorizationUrl) {
    throw new Error('Authorization URL is not defined');
  }

  // Leave comment for future testing
  // const url = new URL('http://localhost:5173');
  const url = new URL('https://internal.zngly.com');

  const searchParams = new URLSearchParams();
  searchParams.set('authorization_url', await getProviderAuthorizationUrl(provider, email));
  searchParams.set('kind', provider.provider);

  url.hash = `#/auth/sso/login?${searchParams.toString()}`;

  window.location.href = url.toString();
};

/**
 * Given a providers authorization URL, we need to modify it:
 * - include state params
 */
const getProviderAuthorizationUrl = async (provider: SSOLoginClient, email?: string): Promise<string> => {
  if (!provider?.authorizationUrl) {
    throw new Error('Authorization URL is not defined');
  }

  const authUrl = new URL(provider?.authorizationUrl);
  const domain = !!email && email.includes('@') ? email.split?.('@')?.[1] : null;

  if (provider.provider === 'GOOGLE_MULTI_TENANT') {
    // remove the hd paramater since we can log in with either zngly.com or client organization email
    if (authUrl.searchParams.has('hd')) {
      authUrl.searchParams.delete('hd');
    }

    if (domain) {
      authUrl.searchParams.set('hd', domain);
    }

    if (!!email) {
      authUrl.searchParams.set('login_hint', email);
    }
  }

  if (provider.provider === 'AZURE_SINGLE_TENANT' || provider.provider === 'AZURE_MULTI_TENANT') {
    const { codeChallenge, codeVerifier } = await generateCodeChallenge();

    // Set the code verifier in session storage
    setCodeVerifier(codeVerifier);

    // Set the code challenge in the authorization URL
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');

    if (!!email) {
      authUrl.searchParams.set('login_hint', email);
    }
  }

  // Leave comment for future testing
  // authUrl.searchParams.set('redirect_uri', 'http://localhost:5173');

  return authUrl.toString();
};

const key = 'zn/sso/pkce_code_verifier';

export const getCodeVerifier = (): string | null => {
  return sessionStorage.getItem(key) || null;
};

export const setCodeVerifier = (value: string | null): void => {
  if (value) {
    sessionStorage.setItem(key, value);
    return;
  }
  sessionStorage.removeItem(key);
};

/**
 * Generates a random code verifier and its corresponding code challenge (S256) for PKCE
 * @returns An object with code verifier and code challenge
 */
const generateCodeChallenge = async (): Promise<{ codeVerifier: string; codeChallenge: string }> => {
  // Generate a random string for the code verifier
  const generateRandomString = (length: number): string => {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let text = '';
    const randomArray = new Uint8Array(length);
    window.crypto.getRandomValues(randomArray);
    for (let i = 0; i < length; i++) {
      text += possible.charAt(randomArray[i] % possible.length);
    }
    return text;
  };

  // Generate code verifier (between 43-128 characters as per spec)
  const codeVerifier = generateRandomString(64);

  // Create code challenge using S256 method
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);

  // Base64-URL encode the digest
  const base64Digest = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return { codeVerifier, codeChallenge: base64Digest };
};
